import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Project,
  ProjectListResponse,
  ProjectListItem,
  CostLocation,
  ProfitCenter,
  ApiResponse,
} from '@/api/types'

/**
 * Projects management service
 */
export class ProjectsService {
  /**
   * Get paginated list of projects for a branch
   */
  async getAllProjects(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<ProjectListResponse> {
    const url = endpoints.projects.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<ProjectListResponse> | ProjectListResponse | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        return payload.data as ProjectListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: payload.data as ProjectListItem[],
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: payload as ProjectListItem[],
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ?? [],
    }
  }

  /**
   * Get project by ID
   */
  async getProjectById(id: number, branchId: number): Promise<Project> {
    const url = endpoints.projects.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<Project> | Project | any>(url)
    
    let projectData: any
    
    if ('data' in response.data && 'success' in response.data) {
      projectData = response.data.data
    } else if ('data' in response.data) {
      projectData = response.data.data
    } else {
      projectData = response.data
    }
    
    return this.mapProjectData(projectData, branchId)
  }

  /**
   * Map API response to Project interface
   */
  private mapProjectData(projectData: any, branchId: number): Project {
    return {
      projectId: projectData.projectId || projectData.id,
      id: projectData.id || projectData.projectId,
      code: projectData.code || '',
      name: projectData.name || projectData.projectName || projectData.projectNameEn || projectData.projectNameAr || '',
      projectName: projectData.projectName || projectData.name || projectData.projectNameEn || projectData.projectNameAr || '',
      projectNameEn: projectData.projectNameEn || projectData.name,
      projectNameAr: projectData.projectNameAr,
      ref: projectData.ref || projectData.reference || '',
      reference: projectData.reference || projectData.ref || '',
      isActive: projectData.isActive,
      costProfitCenter: projectData.costProfitCenter || projectData.costProfitCenterId || 0,
      costProfitCenterName: projectData.costProfitCenterName,
      costLocation: projectData.costLocation || projectData.costLocationId || 0,
      costLocationName: projectData.costLocationName,
      customerId: projectData.customerId || 0,
      customerName: projectData.customerName,
      projectValue: projectData.projectValue || projectData.projectValueExVat || 0,
      projectValueExVat: projectData.projectValueExVat || projectData.projectValue || 0,
      startDate: projectData.startDate || '',
      endDate: projectData.endDate || '',
      duration: projectData.duration || projectData.durationDays || 0,
      durationDays: projectData.durationDays || projectData.duration || 0,
      projectBilled: projectData.projectBilled || 0,
      projectBalance: projectData.projectBalance || 0,
      projectDue: projectData.projectDue || 0,
      projectStatus: projectData.projectStatus || '',
      additionalBillValue: projectData.additionalBillValue || 0,
      companyId: projectData.companyId,
      branchId: projectData.branchId || branchId,
      createdDate: projectData.createdDate,
      createdBy: projectData.createdBy,
      modifiedDate: projectData.modifiedDate,
      modifiedBy: projectData.modifiedBy,
    } as Project
  }

  /**
   * Get cost locations
   */
  async getCostLocations(): Promise<CostLocation[]> {
    const url = endpoints.projects.getCostLocation
    const response = await apiClient.get<ApiResponse<CostLocation[]> | CostLocation[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get profit centers
   */
  async getProfitCenters(): Promise<ProfitCenter[]> {
    const url = endpoints.projects.getProfitCenter
    const response = await apiClient.get<ApiResponse<ProfitCenter[]> | ProfitCenter[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Save project
   */
  async saveProject(project: any): Promise<Project> {
    console.log('=== ProjectsService.saveProject ===')
    console.log('Endpoint:', endpoints.projects.save)
    console.log('Request Payload:', JSON.stringify(project, null, 2))
    console.log('===================================')
    
    const response = await apiClient.post<ApiResponse<Project> | Project>(endpoints.projects.save, project)
    
    console.log('=== API Response ===')
    console.log('Response Status:', response.status)
    console.log('Response Data:', JSON.stringify(response.data, null, 2))
    console.log('====================')
    
    if ('data' in response.data && 'success' in response.data) {
      return (response.data as ApiResponse<Project>).data
    }
    return response.data as Project
  }

  /**
   * Update project
   */
  async updateProject(project: any): Promise<Project> {
    console.log('=== ProjectsService.updateProject ===')
    console.log('Endpoint:', endpoints.projects.update)
    console.log('Request Payload:', JSON.stringify(project, null, 2))
    console.log('====================================')
    
    const response = await apiClient.put<ApiResponse<Project> | Project>(endpoints.projects.update, project)
    
    console.log('=== API Response ===')
    console.log('Response Status:', response.status)
    console.log('Response Data:', JSON.stringify(response.data, null, 2))
    console.log('====================')
    
    if ('data' in response.data && 'success' in response.data) {
      return (response.data as ApiResponse<Project>).data
    }
    return response.data as Project
  }
}

export const projectsService = new ProjectsService()

